import React from 'react'
import { ValidationFunction } from '../components/forms/Validations'

export type FormElementCallback = (
  key: string,
  value: string,
  isRequired: boolean,
  validations?: ValidationFunction[],
) => void

export interface FormContextType {
  onFormElementChange: FormElementCallback
}

export interface FormData {
  [name: string]: string
}

export interface FormDataWithArrayValues {
  [name: string]: string | string[]
}

export interface FormErrors {
  [name: string]: string
}

interface FormState {
  formData: FormData
  formValidations: FormValidations
  isValid: boolean
}

interface FormValidation {
  isRequired: boolean
  validations?: ValidationFunction[]
}

interface FormValidations {
  [name: string]: FormValidation
}

export const flattenErrors = (serverErrors): FormErrors => {
  const errors: FormErrors = {}

  serverErrors?.forEach((error) => {
    if (error.fields) {
      Object.keys(error.fields).forEach((key) => {
        const value = error.fields[key].join(', ')
        errors[key] = value
      })
    }

    if (error.message) {
      errors.message = error.message
    }
  })

  return errors
}

export const formatFormDataForServer = (
  formData: FormData | undefined,
): any => {
  if (formData === undefined) {
    return null
  }
  return Object.keys(formData).reduce((accum, key) => {
    const keys = key.split('.')
    const value =
      typeof formData[key] == 'string' ? formData[key].trim() : formData[key]
    let newValue: string | boolean = value
    if (newValue === 'true') {
      newValue = true
    } else if (newValue === 'false') {
      newValue = false
    }
    const primaryKey = keys[0]
    if (keys.length > 1) {
      const secondaryKey = keys[1]
      accum[primaryKey] = accum[primaryKey] || {}
      accum[primaryKey][secondaryKey] = newValue
    } else {
      accum[primaryKey] = newValue
    }
    return accum
  }, {})
}

export const isValidWhenNullOrFalse = (
  isRequired: boolean | undefined,
  value: string | string[] | null | boolean,
): boolean => {
  return (
    (!isRequired && value !== null && value === '') ||
    (!isRequired && value === null) ||
    (!isRequired && value === undefined) ||
    (!isRequired && value === false) ||
    (!isRequired && value === 'false')
  )
}

export const FormContext = React.createContext<FormContextType>({
  onFormElementChange: () => null,
})

export const useForm = (): {
  formData: FormData
  isValid: boolean
  onFormElementChange: FormElementCallback
  resetForm: () => void
} => {
  const mutableState = React.useRef<FormState>({
    formData: {},
    formValidations: {},
    isValid: false,
  })

  const [{ formData, isValid }, setFormState] = React.useState<FormState>(
    mutableState.current,
  )

  const isFormValid = React.useCallback(() => {
    const {
      formData: mutableFormData,
      formValidations: mutableFormValidations,
    } = mutableState.current

    return Object.keys(mutableFormData).every((key) => {
      let newIsValid = true
      const value = mutableFormData[key]
      const validations = mutableFormValidations[key].validations
      const isRequired = mutableFormValidations[key].isRequired
      if (isValidWhenNullOrFalse(isRequired, value)) {
        newIsValid = true
      } else if (validations && validations.length) {
        newIsValid = validations.every((v) => v(value))
      }
      return newIsValid
    })
  }, [])

  const onFormElementChange = React.useCallback<FormElementCallback>(
    (key, value, isRequired, validations) => {
      const {
        formData: mutableFormData,
        formValidations: mutableFormValidations,
      } = mutableState.current
      mutableFormData[key] = value
      mutableFormValidations[key] = {
        isRequired,
        validations,
      }

      mutableState.current.isValid = isFormValid()
      setFormState({ ...mutableState.current })
    },
    [isFormValid],
  )

  const resetForm = () => {
    setFormState({
      formData: {},
      formValidations: {},
      isValid: false,
    })
  }

  return { formData, isValid, onFormElementChange, resetForm }
}

export default useForm
